/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'

import { Box, Divider, IconButton, SelectChangeEvent, Stack, Typography, useMediaQuery } from '@mui/material';
import dynamic from 'next/dynamic';
import Link from 'next/link';
const Image = dynamic(() => import('@/components/common/image'), { ssr: false });
import * as yup from 'yup';
import { useFormik } from 'formik';
import { POST } from '@/api/post-requests';
const Input = dynamic(() => import('@/components/common/input'), { ssr: false });
import { BUTTONTYPE } from '@/types/button-type';
import { VARIANT } from '@/types/variant';
import React from 'react';
const Button = dynamic(() => import('@/components/common/button'), { ssr: false });
const CheckBox = dynamic(() => import('@/components/common/checkbox'), { ssr: false });
import Cookies from 'js-cookie';
import { useAuth } from '@/context/with-auth';
import { useRouter, useSearchParams } from 'next/navigation';
import { useNotifications } from '@toolpad/core/useNotifications';
import { GET } from '@/api/get-request';
const PhoneInput = dynamic(() => import('@/components/common/phone-input'), { ssr: false });
import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useGoogleAuth } from '@/hooks/useGoogleAuth';
import { loginWithGoogle } from '@/utils/googleAuth';

import '@/components/styles/common.scss';

export default function Page() {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [googleLoading, setGoogleLoading] = React.useState<boolean>(false);
    const [checked, setChecked] = React.useState<boolean>(false);
    const is_mobile = useMediaQuery('(max-width:1024px)');
    const { setUser } = useAuth();
    const notifications = useNotifications();
    const router = useRouter();
    const searchParams = useSearchParams();
    const [phone_codes, setPhoneCodes] = React.useState<{ value: string, label: string }[]>([]);
    const t = useTranslations('signup');
    const ref0 = useRef<HTMLInputElement>(null);
    const ref1 = useRef<HTMLInputElement>(null);
    const ref2 = useRef<HTMLInputElement>(null);
    const ref3 = useRef<HTMLInputElement>(null);
    const callbackUrlRef = useRef<string | null>(null);
    const [callbackUrl, setCallbackUrl] = React.useState<string | null>(null);
    const { isGoogleLoaded, signInWithGoogle, error: googleError } = useGoogleAuth();
    React.useLayoutEffect(() => {

        const GetSelectData = async (url: string) => {
            const response = await GET({ url: `v1/enums/${url}` });
            if (response.success)
                return response.data
            else return []
        };

        const GetData = async () => {

            setPhoneCodes(await GetSelectData('phone_codes'));

        };

        GetData();
    }, []);

    React.useEffect(() => {
        const callbackCandidate = searchParams?.get('callbackUrl');
        if (callbackCandidate && callbackCandidate.startsWith('/')) {
            callbackUrlRef.current = callbackCandidate;
            setCallbackUrl(callbackCandidate);
        } else {
            callbackUrlRef.current = null;
            setCallbackUrl(null);
        }
    }, [searchParams]);

    const validationSchema = yup.object({
        email: yup
            .string()
            .email()
            .required('Email is required'),
        password: yup
            .string()
            .min(8, 'Password must be at least 8 characters')
            .matches(/\d/, 'Password must contain at least one number')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
            .required('Password is required'),
        password_confirmation: yup
            .string()
            .oneOf([yup.ref('password'), ''], 'Passwords must match')
            .required('Confirm password is required'),
        phone: yup
            .string()
            .length(10, 'Phone number must be 10 numbers')
            .required('Phone number is required'),
    });

    const formik = useFormik({
        initialValues: {
            password: "",
            email: "",
            password_confirmation: "",
            phone: "",
            prefix: "CA"
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const response = await POST(
                {
                    url: 'v1/auth/register',
                    body: {
                        email: values.email,
                        phone: '+' + phone_codes.find(item => item.label === values.prefix)?.value + values.phone,
                        password: values.password,
                        password_confirmation: values.password_confirmation
                    }
                }
            );

            if (response.success) {
                if (checked)
                    Cookies.set('token', response.data.token, { expires: 7 });
                else
                    Cookies.set('token', response.data.token);
                setUser(response.data.user);
                const destination = callbackUrlRef.current ?? '/home';
                router.replace(destination);
            } else {
                Object.keys(response.errors).forEach((key) => formik.setFieldError(`${key}`, response.errors[key]));
                notifications.show(response.message, {
                    severity: 'error',
                    autoHideDuration: 3000,
                });
            }

            setLoading(false);


        },
    });

    const handleGoogleSignup = async () => {
        if (!isGoogleLoaded) {
            notifications.show('Google authentication is not ready. Please try again.', {
                severity: 'error',
                autoHideDuration: 3000,
            });
            return;
        }

        if (googleError) {
            notifications.show(`Google authentication error: ${googleError}`, {
                severity: 'error',
                autoHideDuration: 3000,
            });
            return;
        }

        try {
            setGoogleLoading(true);
            const idToken = await signInWithGoogle();

            if (!idToken) {
                notifications.show('Failed to get Google authentication token', {
                    severity: 'error',
                    autoHideDuration: 3000,
                });
                setGoogleLoading(false);
                return;
            }

            // Login/Signup with Google
            const result = await loginWithGoogle({
                idToken,
                rememberMe: checked,
                callbackUrl: callbackUrlRef.current,
                onSuccess: (user, token) => {
                    setUser(user);
                },
                onError: (errorMessage, errors) => {
                    notifications.show(errorMessage, {
                        severity: 'error',
                        autoHideDuration: 3000,
                    });
                },
            });

            if (result.success && result.user) {
                // Check if user needs phone setup
                if (result.needsPhoneSetup) {
                    const googleCallbackUrl = callbackUrlRef.current
                        ? `/login/google?callbackUrl=${encodeURIComponent(callbackUrlRef.current)}`
                        : '/login/google';
                    router.replace(googleCallbackUrl);
                } else {
                    // Redirect to callbackUrl or home
                    const destination = callbackUrlRef.current ?? '/home';
                    router.replace(destination);
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'An error occurred during Google signup';
            notifications.show(errorMessage, {
                severity: 'error',
                autoHideDuration: 3000,
            });
        } finally {
            setGoogleLoading(false);
        }
    };

    return <form className={'signupFrm'} onSubmit={formik.handleSubmit}>
        <Box
            height={'100%'}
        >
            <Stack
                useFlexGap
                spacing={2}
                alignItems={'center'}
            >
                <Typography
                    fontSize={{ lg: 34, sm: 16, md: 16, xs: 16 }}
                    fontWeight={700}
                    marginBottom={2}
                    className={'__title__'}
                >
                    {t('title')}
                </Typography>

                <Stack
                    useFlexGap
                    direction={'row'}
                    spacing={1}
                    alignItems={'center'}
                >
                    <Image
                        alt="person arrow"
                        url="/icons/Person Arrow Right.svg"
                        className="w-5"
                    />

                    <h6>{t('login_button.text')}</h6>
                    <Link
                        href={callbackUrl ? `/login?callbackUrl=${encodeURIComponent(callbackUrl)}` : '/login'}
                    >
                        <h6 className='text-primary'>{t('login_button.action')}</h6>
                    </Link>
                </Stack>


                <IconButton
                    onClick={handleGoogleSignup}
                    disabled={googleLoading || loading || !isGoogleLoaded}
                    sx={{
                        backgroundColor: 'var(--color-grayBackground)',
                        borderRadius: '12px',
                        width: '100%',
                        color: 'var(--color-darkText)',
                        gap: 2,
                        fontSize: { lg: 20, sm: 16, xs: 16, md: 16 },
                        opacity: (!isGoogleLoaded || googleLoading || loading) ? 0.6 : 1,
                        cursor: (!isGoogleLoaded || googleLoading || loading) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {googleLoading ? (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                width: 20,
                                height: 20,
                                border: '2px solid',
                                borderColor: 'var(--color-darkText)',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                },
                            }}
                        />
                    ) : (
                        <Image
                            alt={'google icon'}
                            url={'/icons/google.svg'}
                            className="w-8"
                        />
                    )}
                    {t('google')}
                </IconButton>

                <Divider sx={{ width: '100%' }}>or</Divider>

                <PhoneInput
                    disabled={loading}
                    list={phone_codes.map(cnt => ({ name: cnt.label, value: cnt.value }))}
                    key='phone'
                    label='Number'
                    placeholder='Enter Your Phone Number'
                    id='phone'
                    name='phone'
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    prefix_val={formik.values.prefix}
                    onPrefixChange={(e: SelectChangeEvent<string>) => { formik.setFieldValue('prefix', e.target.value) }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helper_text={formik.touched.phone && formik.errors.phone}
                    inputRef={ref0}
                    onEnter={() => ref1.current?.focus()}
                />

                <Input
                    disabled={loading}
                    start_icon={
                        <Image
                            alt={'mail icon'}
                            url={'/icons/Mail copy.svg'}
                            className="w-5"
                        />
                    }
                    label={t('inputs.email.label')}
                    placeholder={t('inputs.email.placeholder')}
                    id='email'
                    key='email'
                    name='email'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helper_text={formik.touched.email && formik.errors.email}
                    inputRef={ref1}
                    onEnter={() => ref2.current?.focus()}
                />

                <Input
                    disabled={loading}
                    start_icon={
                        <Image
                            alt={'lock icon'}
                            url={'/icons/Lock Closed copy.svg'}
                            className="w-5"
                        />
                    }
                    password
                    label={t('inputs.password.label')}
                    placeholder={t('inputs.password.placeholder')}
                    id='password'
                    key='password'
                    name='password'
                    inputRef={ref2}
                    onEnter={() => ref3.current?.focus()}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helper_text={formik.touched.password && formik.errors.password || t('inputs.password.validation')}
                />

                <Input
                    disabled={loading}
                    start_icon={
                        <Image
                            alt={'lock icon'}
                            url={'/icons/Lock Closed copy.svg'}
                            className="w-5"
                        />
                    }
                    password
                    label=''
                    inputRef={ref3}
                    onEnter={() => formik.handleSubmit()}
                    placeholder={t('inputs.password_confirmation.placeholder')}
                    id='password_confirmation'
                    key='password_confirmation'
                    name='password_confirmation'
                    value={formik.values.password_confirmation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password_confirmation && Boolean(formik.errors.password_confirmation)}
                    helper_text={formik.touched.password_confirmation && formik.errors.password_confirmation}
                />

                <div className={'boxAgrmnt'}>
                    <Box
                        sx={{
                            marginRight: 'auto'
                        }}
                    >
                        <CheckBox
                            label={t('checkbox_text')}
                            checked={checked}
                            onChange={setChecked}
                        />
                    </Box>

                    <Typography
                        variant={is_mobile ? 'caption' : 'body2'}
                    >
                        By clicking Create account, <br />I agree that I have read and accepted the
                        <a href="https://canapply.ca/policy/terms" className='text-blue cursor-pointer'>Terms of Use</a> and
                        <a href='https://canapply.ca/policy/privacy' className='text-blue cursor-pointer'>Privacy Policy</a>.
                    </Typography>
                </div>
                <Button
                    loading={loading}
                    aria_label="sign un button"
                    text={t('button_text')}
                    handleClick={() => { }}
                    type={BUTTONTYPE.SUBMIT}
                    key={'sign un button'}
                    variant={VARIANT.CONTAINED}
                    className=' w-full'
                    borderRadius={is_mobile ? 8 : 12}
                />

            </Stack>
        </Box>
    </form>
};
